import { isRealTenantId } from "@/src/lib/tenantSlug";
import { createContext, FunctionComponent, ReactNode, useEffect, useState } from "react";
import queryClient from "../queryClient";
import { jwtDecode } from "jwt-decode";
import { UserRole } from "../types";
import useLocalStorage from "../hooks/useLocalStorage";
import { getNavigate } from "../routes/navigation";

export type AuthType = {
  accessToken: string;
  refreshToken: string;
  isLoggedIn: boolean;
  role: UserRole;
  tenantId: string;
  userId: string;
  isInhabitant: boolean;
  isSuperadmin: boolean;
  isAdmin: boolean;
  isConsultant: boolean;
};

type AccessTokenData = {
  role: UserRole;
  userId: string;
} & { role: Exclude<UserRole, "superadmin">; tenantId: string } & Record<string, string | number>;

type SignInData = Pick<AuthType, "accessToken" | "refreshToken" | "isLoggedIn">;

export type AuthContextType = {
  auth: AuthType;
  setAuth: (value: AuthType) => void;
  onSignIn: (value: SignInData) => void;
  logout: (redirectTenantId?: string) => void;
};

const initialValues = {
  accessToken: "",
  refreshToken: "",
  role: "inhabitant",
  isLoggedIn: false,
  userId: "",
  tenantId: "",
  isInhabitant: false,
  isSuperadmin: false,
  isAdmin: false,
  isConsultant: false,
} satisfies AuthType;

const AuthContext = createContext<AuthContextType>({
  auth: initialValues,
  onSignIn: () => {},
  setAuth: () => {},
  logout: () => {},
});

/**
 * Gdzie trzymamy sesję:
 *  - admin gminy i superadmin — localStorage: przeżywa zamknięcie karty /
 *    przeglądarki, nowe karty startują zalogowane (admina nie wylogowujemy;
 *    refresh token admina żyje rok, patrz api auth.service),
 *  - pozostali — sessionStorage jak dotąd (sesja per karta).
 * Klucz ten sam co wcześniej w useSessionStorage("auth").
 */
const AUTH_STORAGE_KEY = "tenant:auth";

const persistsAcrossSessions = (a: AuthType) => a.isLoggedIn && (a.isAdmin || a.isSuperadmin);

function readStoredAuth(): AuthType {
  if (typeof window === "undefined") return initialValues;
  for (const storage of [window.sessionStorage, window.localStorage]) {
    try {
      const raw = storage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as AuthType) : null;
      if (parsed?.isLoggedIn) return parsed;
    } catch {
      /* uszkodzony wpis — sprawdź kolejny magazyn */
    }
  }
  return initialValues;
}

function writeStoredAuth(a: AuthType) {
  try {
    if (persistsAcrossSessions(a)) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(a));
      window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
    } else if (a.isLoggedIn) {
      window.sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(a));
    } else {
      // Wylogowanie — czyścimy oba magazyny (także trwałą sesję admina).
      window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch {
    /* brak dostępu do storage (tryb prywatny) — sesja tylko w pamięci */
  }
}

const checkRole = (role: UserRole) => {
  return {
    isInhabitant: role === "inhabitant",
    isSuperadmin: role === "superadmin",
    isAdmin: role === "admin",
    isConsultant: role === "consultant",
  };
};

export const AuthProvider: FunctionComponent<{
  children: ReactNode;
}> = ({ children }) => {
  const [auth, setAuthState] = useState<AuthType>(readStoredAuth);
  const setAuth = (value: AuthType) => {
    setAuthState(value);
    writeStoredAuth(value);
  };
  // Synchronizacja trwałej sesji admina między kartami: refresh w jednej
  // karcie rotuje refresh token — pozostałe muszą dostać nowy, inaczej ich
  // kolejny refresh (starym tokenem) zakończyłby się wylogowaniem.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== window.localStorage || e.key !== AUTH_STORAGE_KEY) return;
      setAuthState((current) => {
        if (!persistsAcrossSessions(current)) return current;
        if (!e.newValue) return initialValues; // wylogowanie w innej karcie
        try {
          const next = JSON.parse(e.newValue) as AuthType;
          return next?.userId === current.userId ? next : current;
        } catch {
          return current;
        }
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const [mainTenant, setMainTenant] = useLocalStorage<string>("mainTenant", "");
  const navigate = getNavigate();
  const onSignIn = (data: SignInData) => {
    const { role, tenantId, userId } = jwtDecode<AccessTokenData>(data.accessToken);

    setAuth({
      ...data,
      role,
      tenantId,
      userId,
      ...checkRole(role),
    });
    // Zapisujemy w localStorage AKTUALNY tenantId z JWT żeby logout/redirect
    // prowadzil na /$tenant/login danej gminy. Nadpisujemy ewentualny stary
    // (np. po migracji tenant ID w DB).
    setMainTenant(isRealTenantId(tenantId) ? tenantId : "");
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        onSignIn,
        logout: (redirectTenantId?: string) => {
          setAuth(initialValues);
          queryClient.clear();
          // Preferuj tenantId z aktualnej sesji (auth.tenantId) nad mainTenant
          // z localStorage — żeby nie zostac rzucony na /stary_id/login po
          // migracji ID gminy. Jezeli auth nie ma tenantId (superadmin)
          // idziemy na /admin/login.
          const redirectTenant =
            redirectTenantId || (isRealTenantId(auth.tenantId) ? auth.tenantId : mainTenant);
          if (isRealTenantId(redirectTenantId)) setMainTenant(redirectTenantId);
          if (redirectTenant) {
            navigate({ to: "/$tenant/login", params: { tenant: redirectTenant } });
          } else {
            navigate({ to: "/admin/login" });
          }
        },
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
